import { useMemo, useState } from 'react';
import { formatPercent, formatPrice } from '../lib/format';
import { downloadScannerCsv, snapshotToRow, type SnapshotRow } from '../lib/marketMetrics';
import type { Signal, SymbolSnapshot } from '../types/market';

type SortKey = 'symbol' | 'changePct' | 'score' | 'confidence' | 'rsi' | 'volumeRatio' | 'pmChangePct' | 'pmVolume';

export function MarketScanner({ snapshots, onSelect }: { snapshots: Record<string, SymbolSnapshot>; onSelect: (symbol: string) => void }) {
  const [filter, setFilter] = useState<'ALL' | Signal>('ALL');
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [descending, setDescending] = useState(true);

  const rows = useMemo(() => {
    const data = Object.values(snapshots).map(snapshotToRow)
      .filter((row) => (!query || row.symbol.includes(query.trim().toUpperCase())) && (filter === 'ALL' || row.signal === filter));
    return [...data].sort((a, b) => compare(a, b, sortKey, descending));
  }, [descending, filter, query, snapshots, sortKey]);

  function sort(key: SortKey) {
    if (sortKey === key) setDescending((value) => !value);
    else {
      setSortKey(key);
      setDescending(key !== 'symbol');
    }
  }

  return (
    <section className="scanner-panel">
      <div className="panel-heading-row">
        <div>
          <span className="eyebrow">Signal discovery</span>
          <h2>Market Scanner</h2>
        </div>
        <div className="scanner-controls">
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter symbol" aria-label="Filter scanner by symbol" />
          <select value={filter} onChange={(event) => setFilter(event.target.value as 'ALL' | Signal)}>
            <option value="ALL">All signals</option>
            <option value="BUY">BUY</option>
            <option value="SELL">SELL</option>
            <option value="HOLD">HOLD</option>
          </select>
          <button className="action-link" type="button" onClick={() => downloadScannerCsv(rows)}>Export CSV</button>
        </div>
      </div>

      <div className="scanner-table-wrap">
        <table className="scanner-table">
          <thead><tr>
            <Sortable label="Symbol" active={sortKey === 'symbol'} descending={descending} onClick={() => sort('symbol')} />
            <th>Price</th>
            <Sortable label="Change" active={sortKey === 'changePct'} descending={descending} onClick={() => sort('changePct')} />
            <Sortable label="PM Change" active={sortKey === 'pmChangePct'} descending={descending} onClick={() => sort('pmChangePct')} />
            <Sortable label="PM Vol" active={sortKey === 'pmVolume'} descending={descending} onClick={() => sort('pmVolume')} />
            <th>Signal</th>
            <Sortable label="Confidence" active={sortKey === 'confidence'} descending={descending} onClick={() => sort('confidence')} />
            <Sortable label="Score" active={sortKey === 'score'} descending={descending} onClick={() => sort('score')} />
            <th>State</th>
            <Sortable label="RSI" active={sortKey === 'rsi'} descending={descending} onClick={() => sort('rsi')} />
            <Sortable label="Rel Vol" active={sortKey === 'volumeRatio'} descending={descending} onClick={() => sort('volumeRatio')} />
          </tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.symbol} onClick={() => onSelect(row.symbol)}>
                <td><strong>{row.symbol}</strong></td>
                <td>{formatPrice(row.price)}</td>
                <td className={(row.changePct ?? 0) < 0 ? 'negative' : 'positive'}>{formatPercent(row.changePct)}</td>
                <td className={(row.pmChangePct ?? 0) < 0 ? 'negative' : (row.pmChangePct ?? 0) > 0 ? 'positive' : ''}>{row.pmChangePct != null ? formatPercent(row.pmChangePct) : '—'}</td>
                <td className="val-neutral">{row.pmVolume != null ? (row.pmVolume >= 1000000 ? (row.pmVolume / 1000000).toFixed(2) + 'M' : row.pmVolume >= 1000 ? (row.pmVolume / 1000).toFixed(1) + 'K' : row.pmVolume) : '—'}</td>
                <td><span className={`signal-badge signal-${(row.signal ?? 'HOLD').toLowerCase()}`}>{row.signal ?? '—'}</span></td>
                <td>{row.confidence == null ? '—' : `${Math.round(row.confidence * 100)}%`}</td>
                <td>{row.score == null ? '—' : row.score.toFixed(2)}</td>
                <td><span className="state-chip">{row.state?.replace('_', ' ') ?? '—'}</span></td>
                <td>{row.rsi == null ? '—' : row.rsi.toFixed(1)}</td>
                <td>{row.volumeRatio == null ? '—' : `${row.volumeRatio.toFixed(2)}×`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Sortable({ label, active, descending, onClick }: { label: string; active: boolean; descending: boolean; onClick: () => void }) {
  return <th><button className="sort-button" type="button" onClick={onClick}>{label}{active ? (descending ? ' ↓' : ' ↑') : ''}</button></th>;
}

function compare(a: SnapshotRow, b: SnapshotRow, key: SortKey, descending: boolean): number {
  const av = a[key];
  const bv = b[key];
  let result = 0;
  if (typeof av === 'string' || typeof bv === 'string') result = String(av ?? '').localeCompare(String(bv ?? ''));
  else result = (Number(av ?? -Infinity) - Number(bv ?? -Infinity));
  return descending ? -result : result;
}
